import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type EquipmentAttributes = {
	imei?: string | null;
	serialNumber?: string | null;
};

type EquipmentSummary = {
	brand?: string | null;
	model?: string | null;
	attributes?: EquipmentAttributes | null;
};

type TechnicianSummary = {
	external?: boolean | null;
};

type RepairLogEntry = {
	type?: string | null;
	message?: string | null;
};

type RepairEntry = {
	id?: string | number | null;
	storeRepairIdentifier?: string | null;
	repairNumber?: string | number | null;
	createdAt?: string | Date | null;
	clientEquipment?: EquipmentSummary | null;
	problemDescription?: string | null;
	quotation?: number | null;
	value?: number | null;
	technician?: TechnicianSummary | null;
	quotationApproved?: boolean | null;
	state?: string | null;
	materials?: Array<unknown> | null;
	repairObservation?: string | null;
	logs?: RepairLogEntry[] | null;
};

type ClientOngoingTableProps = {
	repairs?: RepairEntry[];
	loading?: boolean;
	previsionDates?: Record<string, string>;
	onApproveQuotation: (repair: RepairEntry) => void;
	onRejectQuotation: (repair: RepairEntry) => void;
};

const formatEquipmentName = (equipment?: EquipmentSummary | null) => {
	if (!equipment) {
		return "-";
	}

	const brand = equipment.brand?.trim() ?? "";
	const model = equipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();
	return label.length > 0 ? label : "-";
};

const formatImei = (equipment?: EquipmentSummary | null) => {
	const attributes = equipment?.attributes;
	const imei = attributes?.imei ?? "";
	const serial = attributes?.serialNumber ?? "";
	const value = imei.length > 0 ? imei : serial;
	return value.length > 0 ? value : "-";
};

const formatDate = (value?: string | Date | null) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, "dd/MM/yyyy");
};

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const logsFilter = (logs?: RepairLogEntry[] | null) => {
	if (!logs) {
		return "-";
	}

	const repairLogs = logs.filter((item) => item.type === "repair.quotation");
	const entry = repairLogs[0];
	return entry?.message ?? " - ";
};

const repairTime = (repair?: RepairEntry | null) => {
	const entry = logsFilter(repair?.logs ?? []);
	const parts = entry.split("-");
	const time = parts[1];
	if (!time) {
		return "-";
	}
	let finalMessage = time.trim();
	const observation = parts.slice(2).join("-").trim();
	if (observation && observation.length > 9) {
		finalMessage += ` - ${observation}`;
	}
	return finalMessage;
};

const canShowApproveQuotation = (repair: RepairEntry) => {
	const technician = repair.technician;
	const isExternal = technician?.external ?? false;
	return (
		repair.state === "waiting_quotation_approve" &&
		((isExternal && repair.value) || !isExternal)
	);
};

const isInRepair = (repair: RepairEntry) =>
	!canShowApproveQuotation(repair) &&
	repair.state !== "waiting_delivery" &&
	!repair.quotationApproved &&
	repair.state !== "waiting_material" &&
	repair.state !== "in_tests";

export function ClientOngoingTable({
	repairs = [],
	loading = false,
	previsionDates = {},
	onApproveQuotation,
	onRejectQuotation,
}: ClientOngoingTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`ongoing-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[75px]">#Loja</TableHead>
						<TableHead className="w-[90px]">#Rep</TableHead>
						<TableHead className="w-[115px]">DataEntrada</TableHead>
						<TableHead className="w-[160px]">Equipamento</TableHead>
						<TableHead className="w-[160px]">IMEI/SN</TableHead>
						<TableHead>Problema</TableHead>
						<TableHead className="w-[200px]">Orçamento</TableHead>
						<TableHead className="w-[115px]">Previsão</TableHead>
						<TableHead className="w-[200px]">Estado</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : (
					<TableBody>
						{repairs.map((repair) => {
							const rowKey = repair.id ?? repair.repairNumber ?? "ongoing-row";
							const timeLabel = repairTime(repair);
							const showQuotation =
								repair.quotation &&
								(!repair.technician || !repair.technician.external);
							const showValue = Boolean(repair.value);
							const dateKey = String(repair.id ?? "");
							const prevision = previsionDates[dateKey] ?? "A carregar...";

							return (
								<TableRow key={rowKey}>
									<TableCell>{repair.storeRepairIdentifier ?? "-"}</TableCell>
									<TableCell>{repair.repairNumber ?? "-"}</TableCell>
									<TableCell>{formatDate(repair.createdAt)}</TableCell>
									<TableCell>
										{formatEquipmentName(repair.clientEquipment)}
									</TableCell>
									<TableCell>{formatImei(repair.clientEquipment)}</TableCell>
									<TableCell>{repair.problemDescription ?? "-"}</TableCell>
									<TableCell>
										{timeLabel === "-"
											? "-"
											: showQuotation
												? `${formatCurrency(normalizeNumber(repair.quotation))} - ${
														timeLabel
													}`
												: showValue
													? `${formatCurrency(normalizeNumber(repair.value))} - ${
															timeLabel
														}`
													: "-"}
									</TableCell>
									<TableCell>{repair.materials ? prevision : "-"}</TableCell>
									<TableCell>
										<div className="space-y-1">
											{repair.state === "waiting_delivery" ? (
												<span>{repair.repairObservation}</span>
											) : null}
											{repair.quotationApproved ? <span>Aprovado</span> : null}
											{repair.quotationApproved === false &&
											repair.quotation &&
											!canShowApproveQuotation(repair) ? (
												<span>Orçamento rejeitado</span>
											) : null}
											{repair.quotationApproved !== false &&
											isInRepair(repair) ? (
												<span>Em reparação</span>
											) : null}
											{canShowApproveQuotation(repair) ? (
												<div className="flex flex-wrap items-center gap-2">
													<span>Espera aprovar orçamento</span>
													<Button
														size="sm"
														variant="secondary"
														onClick={() => onApproveQuotation(repair)}
													>
														Aprovar
													</Button>
													<Button
														size="sm"
														variant="destructive"
														onClick={() => onRejectQuotation(repair)}
													>
														Recusar
													</Button>
												</div>
											) : null}
											{repair.state === "waiting_material" ? (
												<span>Espera material</span>
											) : null}
											{repair.state === "in_tests" ? (
												<span>Em testes</span>
											) : null}
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				)}
			</Table>
		</div>
	);
}
